import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface BlogPost {
  title: string;
  content: string;
  h2Titles?: string[];
}

interface H2Title {
  title: string;
  editable: boolean;
}

interface BlogCreatorTabProps {
  prefilledData?: any;
  onDataUsed?: () => void;
}

const BlogCreatorTab: React.FC<BlogCreatorTabProps> = ({ prefilledData, onDataUsed }) => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form inputs
  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('General readers');
  const [tone, setTone] = useState('Professional');
  const [length, setLength] = useState('Medium (800-1200 words)');
  
  // H2 Structure Control
  const [h2Method, setH2Method] = useState<'none' | 'manual' | 'ai' | 'keyword'>('none');
  const [h2Titles, setH2Titles] = useState<H2Title[]>([]);
  const [seedKeywords, setSeedKeywords] = useState<string>('');
  const [generatingH2, setGeneratingH2] = useState(false);
  
  // Keywords
  const [keywords, setKeywords] = useState<string>('');
  const [keywordsPerSection, setKeywordsPerSection] = useState<number>(5);
  
  // Formatting options
  const [enableLists, setEnableLists] = useState(false);
  const [enableFAQ, setEnableFAQ] = useState(false);
  const [enableTables, setEnableTables] = useState(false);
  
  // Editor states
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [editMode, setEditMode] = useState(false);
  const contentEditorRef = useRef<HTMLDivElement>(null);

  // Auto-populate from prefilledData
  useEffect(() => {
    if (prefilledData) {
      setTopic(prefilledData.topic || '');
      setDescription(prefilledData.description || '');
      setTargetAudience(prefilledData.targetAudience || 'General readers');
      setLength(prefilledData.length || 'Medium (800-1200 words)');
      
      if (prefilledData.keywords && Array.isArray(prefilledData.keywords)) {
        setKeywords(prefilledData.keywords.join(', '));
      }
      
      if (prefilledData.enableFAQ !== undefined) setEnableFAQ(prefilledData.enableFAQ);
      if (prefilledData.enableTables !== undefined) setEnableTables(prefilledData.enableTables);
      if (prefilledData.enableLists !== undefined) setEnableLists(prefilledData.enableLists);
      
      // Call onDataUsed to clear the prefilled data after use
      if (onDataUsed) {
        onDataUsed();
      }

      // Show success message
      toast.success('Form auto-populated from topical authority!', { duration: 3000 });
    }
  }, [prefilledData, onDataUsed]);

  // Handle H2 generation
  const handleGenerateH2 = async () => {
    if (!topic || !description) {
      toast.error('Please provide topic and description first');
      return;
    }

    setGeneratingH2(true);
    try {
      const userId = await getToken();
      if (!userId) {
        toast.error('Authentication error');
        return;
      }

      const payload: any = {
        userId,
        topic,
        description,
        method: h2Method,
        count: 5
      };

      if (h2Method === 'keyword' && seedKeywords.trim()) {
        payload.seedKeywords = seedKeywords.split(',').map(k => k.trim()).filter(k => k);
      }

      const response = await fetch(
        `${ENV.API_URL}/api/marketing/blog-post/generate-h2`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate H2 titles');
      }

      const data = await response.json();
      setH2Titles(data.h2Titles.map((title: string) => ({ title, editable: true })));
      toast.success('H2 titles generated!');
    } catch (error) {
      console.error('Error generating H2 titles:', error);
      toast.error('Failed to generate H2 titles');
    } finally {
      setGeneratingH2(false);
    }
  };

  const addManualH2 = () => {
    setH2Titles([...h2Titles, { title: '', editable: true }]);
  };

  const updateH2Title = (index: number, newTitle: string) => {
    const updated = [...h2Titles];
    updated[index].title = newTitle;
    setH2Titles(updated);
  };

  const removeH2Title = (index: number) => {
    setH2Titles(h2Titles.filter((_, i) => i !== index));
  };

  // Save post to Content Library (localStorage)
  const saveToLibrary = (post: BlogPost) => {
    if (!user) return;

    const savedPost = {
      id: `post_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: post.title,
      content: post.content,
      topic,
      keywords: keywords.trim() ? keywords.split(',').map(k => k.trim()).filter(k => k) : [],
      length,
      tone,
      targetAudience,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Load existing posts
    const existingPosts = localStorage.getItem(`seo_content_library_${user.id}`);
    const posts = existingPosts ? JSON.parse(existingPosts) : [];

    // Add new post
    posts.unshift(savedPost);

    // Save back to localStorage
    localStorage.setItem(`seo_content_library_${user.id}`, JSON.stringify(posts));
    
    console.log('Post saved to Content Library:', savedPost.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!topic || !description) {
      toast.error('Please provide both a topic and description');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Creating your blog post...');

      const userId = await getToken();
      
      if (!userId) {
        toast.error('Authentication error. Please log in again.');
        navigate('/login');
        return;
      }

      const response = await fetch(
        `${ENV.API_URL}/api/marketing/blog-post`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId,
            topic,
            description,
            targetAudience,
            tone,
            length,
            h2Titles: h2Titles.length > 0 ? h2Titles.map(h => h.title).filter(t => t) : undefined,
            keywords: keywords.trim() ? keywords.split(',').map(k => k.trim()).filter(k => k) : undefined,
            keywordsPerSection,
            enableLists,
            enableFAQ,
            enableTables
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate blog post');
      }

      const data = await response.json();
      toast.dismiss();
      setBlogPost(data.blogPost);
      setEditedContent(data.blogPost.content);
      
      // Auto-save to Content Library
      saveToLibrary(data.blogPost);
      
      toast.success('Blog post created successfully!');
    } catch (error) {
      toast.dismiss();
      console.error('Error creating blog post:', error);
      toast.error(error instanceof Error ? error.message : 'An error occurred while creating your blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (!editMode && contentEditorRef.current) {
      contentEditorRef.current.focus();
    }
  };

  const handleContentChange = (e: React.FormEvent<HTMLDivElement>) => {
    setEditedContent(e.currentTarget.innerHTML);
  };

  const copyToClipboard = () => {
    if (blogPost) {
      const content = editMode ? editedContent : blogPost.content;
      const fullContent = `${blogPost.title}\n\n${content.replace(/<[^>]*>/g, '')}`;
      navigator.clipboard.writeText(fullContent);
      toast.success('Blog post copied to clipboard as plain text!');
    }
  };
  
  const copyHtmlToClipboard = () => {
    if (blogPost) {
      const content = editMode ? editedContent : blogPost.content;
      const fullHtmlContent = `<h1>${blogPost.title}</h1>\n${content}`;
      navigator.clipboard.writeText(fullHtmlContent);
      toast.success('HTML copied to clipboard!');
    }
  };

  const newPost = () => {
    setBlogPost(null);
    setEditedContent('');
    setTopic('');
    setDescription('');
    setH2Titles([]);
    setKeywords('');
  };

  if (!blogPost) {
    return (
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left column - Input form */}
        <div className="w-full lg:w-2/5 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-5">Blog Details</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Topic *
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
                placeholder="What is your blog post about?"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800 h-24"
                placeholder="Brief description of the content..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Target Audience
              </label>
              <select
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
              >
                <option value="General readers">General readers</option>
                <option value="Business professionals">Business professionals</option>
                <option value="Technical experts">Technical experts</option>
                <option value="Beginners">Beginners</option>
                <option value="Students">Students</option>
                <option value="Entrepreneurs">Entrepreneurs</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tone
              </label>
              <select
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              >
                <option value="Professional">Professional</option>
                <option value="Conversational">Conversational</option>
                <option value="Educational">Educational</option>
                <option value="Persuasive">Persuasive</option>
                <option value="Inspirational">Inspirational</option>
                <option value="Humorous">Humorous</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Length
              </label>
              <select
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800"
                value={length}
                onChange={(e) => setLength(e.target.value)}
              >
                <option value="Short (300-500 words)">Short (300-500 words)</option>
                <option value="Medium (800-1200 words)">Medium (800-1200 words)</option>
                <option value="Long (1500-2000 words)">Long (1500-2000 words)</option>
              </select>
            </div>

            {/* H2 Structure Control */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                H2 Structure (Optional)
              </label>
              <select
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800 mb-3"
                value={h2Method}
                onChange={(e) => {
                  setH2Method(e.target.value as any);
                  setH2Titles([]);
                }}
              >
                <option value="none">Auto-generate structure</option>
                <option value="manual">Manual entry</option>
                <option value="ai">AI-generated</option>
                <option value="keyword">From keywords</option>
              </select>

              {h2Method === 'keyword' && (
                <div className="mb-3">
                  <input
                    type="text"
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800 text-sm"
                    placeholder="Seed keywords (comma-separated)"
                    value={seedKeywords}
                    onChange={(e) => setSeedKeywords(e.target.value)}
                  />
                </div>
              )}

              {(h2Method === 'ai' || h2Method === 'keyword') && (
                <button
                  type="button"
                  onClick={handleGenerateH2}
                  disabled={generatingH2}
                  className="w-full py-2 px-4 bg-white text-blue-700 border border-gray-300 rounded-lg shadow-sm hover:shadow transition-all duration-200 text-sm mb-3"
                >
                  {generatingH2 ? 'Generating...' : 'Generate H2 Titles'}
                </button>
              )}

              {h2Method === 'manual' && (
                <button
                  type="button"
                  onClick={addManualH2}
                  className="w-full py-2 px-4 bg-white text-blue-700 border border-gray-300 rounded-lg shadow-sm hover:shadow transition-all duration-200 text-sm mb-3"
                >
                  + Add H2 Title
                </button>
              )}

              {h2Titles.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {h2Titles.map((h2, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800 text-sm"
                        placeholder={`H2 #${index + 1}`}
                        value={h2.title}
                        onChange={(e) => updateH2Title(index, e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removeH2Title(index)}
                        className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors text-sm"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Keywords */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                SEO Keywords (Optional)
              </label>
              <textarea
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800 text-sm h-20 mb-2"
                placeholder="Enter keywords separated by commas"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
              />
              {keywords.trim() && (
                <div className="text-xs text-gray-600 mb-2">
                  {keywords.split(',').filter(k => k.trim()).length} keywords • {keywordsPerSection} per section
                </div>
              )}
              <input
                type="number"
                min="1"
                max="10"
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-800 text-sm"
                placeholder="Keywords per section"
                value={keywordsPerSection}
                onChange={(e) => setKeywordsPerSection(parseInt(e.target.value) || 5)}
              />
            </div>

            {/* Formatting Options */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Formatting Options
              </label>
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableLists}
                    onChange={(e) => setEnableLists(e.target.checked)}
                    className="mr-2 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Enable bullet point lists</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableTables}
                    onChange={(e) => setEnableTables(e.target.checked)}
                    className="mr-2 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Enable tables</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableFAQ}
                    onChange={(e) => setEnableFAQ(e.target.checked)}
                    className="mr-2 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Add FAQ section (5 questions)</span>
                </label>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                className="w-full py-3 px-6 border border-transparent rounded-xl shadow-lg text-white font-medium text-base bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-200"
                disabled={loading}
              >
                {loading ? 'Generating...' : 'Create Blog Post'}
              </button>
            </div>
          </form>
        </div>

        {/* Right column - Preview/Instructions */}
        <div className="w-full lg:w-3/5 bg-white rounded-xl p-6 border border-gray-200">
          <div className="text-center py-12">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No blog post yet</h3>
            <p className="text-gray-500 mb-4">Fill out the form and click "Create Blog Post" to generate your content.</p>
            
            <div className="mt-8 text-left max-w-md mx-auto">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">✨ Features:</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>AI-powered content generation with Claude 3.7 Sonnet</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>H2 structure control (4 methods)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Keyword optimization (up to 150 keywords)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Formatting options (lists, tables, FAQ)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Editable content with HTML export</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left column - Metadata */}
      <div className="w-full lg:w-2/5 space-y-4">
        <div className="bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Blog Post Info</h3>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="font-semibold text-gray-700">Topic:</dt>
              <dd className="text-gray-600 mt-1">{topic}</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-700">Audience:</dt>
              <dd className="text-gray-600 mt-1">{targetAudience}</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-700">Tone:</dt>
              <dd className="text-gray-600 mt-1">{tone}</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-700">Length:</dt>
              <dd className="text-gray-600 mt-1">{length}</dd>
            </div>
            {keywords && (
              <div>
                <dt className="font-semibold text-gray-700">Keywords:</dt>
                <dd className="text-gray-600 mt-1">{keywords.split(',').length} keywords</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="space-y-2">
          <button
            onClick={handleEditToggle}
            className="w-full py-2 px-4 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            {editMode ? 'Stop Editing' : 'Edit Content'}
          </button>
          <button
            onClick={copyToClipboard}
            className="w-full py-2 px-4 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Copy as Text
          </button>
          <button
            onClick={copyHtmlToClipboard}
            className="w-full py-2 px-4 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Copy as HTML
          </button>
          <button
            onClick={newPost}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Create New Post
          </button>
        </div>
      </div>

      {/* Right column - Content */}
      <div className="w-full lg:w-3/5 bg-white rounded-xl p-6 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{blogPost.title}</h2>
        <div
          ref={contentEditorRef}
          contentEditable={editMode}
          onInput={handleContentChange}
          className={`prose max-w-none ${editMode ? 'border-2 border-blue-400 rounded-lg p-4' : ''}`}
          dangerouslySetInnerHTML={{ __html: editMode ? editedContent : blogPost.content }}
        />
      </div>
    </div>
  );
};

export default BlogCreatorTab;
